import { router, useLocalSearchParams } from 'expo-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import {
  SafeAreaView,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';

import {
  APP_BOTTOM_TAB_BAR_BASE_HEIGHT,
  AuthenticatedBottomTabBar,
  getStableBottomInset,
} from '@/src/components/AuthenticatedBottomTabBar';
import { AuthenticatedRouteTransition } from '@/src/components/AuthenticatedRouteTransition';
import { ErrorView } from '@/src/components/ErrorView';
import { InternalNotFound } from '@/src/components/InternalNotFound';
import { LoadingView } from '@/src/components/LoadingView';
import { Screen } from '@/src/components/Screen';
import { useToast } from '@/src/components/Toast';
import { useAuth } from '@/src/features/auth/use-auth';
import { CommentComposeModal } from '@/src/features/comments/CommentComposeModal';
import {
  CommentComposer,
  CommentComposerCard,
  type CommentComposerProps,
} from '@/src/features/comments/CommentComposer';
import { COMMENT_MODAL_COMPACT_BREAKPOINT } from '@/src/features/comments/CommentModalShell';
import { CommentSortControl } from '@/src/features/comments/CommentSortControl';
import type { CommentReplyTarget } from '@/src/features/comments/comment-threading';
import { PostCommentThreadList } from '@/src/features/comments/PostCommentThreadList';
import { ReplyCommentComposer } from '@/src/features/comments/ReplyCommentComposer';
import { useCommentComposerState } from '@/src/features/comments/use-comment-composer-state';
import { useCommentSortPreference } from '@/src/features/comments/use-comment-sort-preference';
import { GiphyPickerModal } from '@/src/features/giphy/GiphyPickerModal';
import { PostCard } from '@/src/features/posts/PostCard';
import { PostPhotoViewerModal } from '@/src/features/posts/PostPhotoViewerModal';
import { PostVideoViewerModal } from '@/src/features/posts/PostVideoViewerModal';
import {
  isNetworkAvailable,
  isRecoverableNetworkError,
  queueCreateComment,
  queueDeleteComment,
} from '@/src/features/offline/outbox-service';
import {
  MAX_COMMENT_BODY_LENGTH,
  MAX_VIDEO_DURATION_MILLIS,
  MAX_VIDEO_DURATION_SECONDS,
  type PublicPost,
} from '@/src/features/posts/post-service';
import {
  useCreatePostComment,
  useDeletePostComment,
  usePostComments,
} from '@/src/features/posts/use-post-comments';
import { usePost, usePublicPostDetail } from '@/src/features/posts/use-post';
import { usePublicPosts } from '@/src/features/posts/use-public-posts';
import { getProfileAvatarURL } from '@/src/features/profile/profile-service';
import { useProfile } from '@/src/features/profile/use-profile';
import { maybeCorrectWriting } from '@/src/features/writing-assist/writing-assist-service';
import { trackEvent } from '@/src/lib/analytics';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

const NEW_COMMENT_SCROLL_THRESHOLD = 160;

export default function PostDetailScreen() {
  const params = useLocalSearchParams<{
    commentId?: string;
    postId?: string;
  }>();
  const postId = Array.isArray(params.postId)
    ? params.postId[0]
    : params.postId;
  const targetCommentId = Array.isArray(params.commentId)
    ? params.commentId[0]
    : params.commentId;
  const { user } = useAuth();
  const signedIn = Boolean(user?.uid);
  const { showError, showSuccess } = useToast();
  const profileQuery = useProfile(user?.uid);
  const postState = usePost(postId, { enabled: signedIn });
  const publicPostState = usePublicPostDetail(postId, !signedIn);
  const commentsState = usePostComments(postId, user?.uid, 50, {
    enabled: signedIn,
  });
  const createComment = useCreatePostComment();
  const deleteComment = useDeletePostComment();
  const { setSortMode, sortMode } = useCommentSortPreference();
  const commentComposerState = useCommentComposerState({
    giphySelectEventName: 'giphy_select',
    maxVideoDurationMillis: MAX_VIDEO_DURATION_MILLIS,
    maxVideoDurationSeconds: MAX_VIDEO_DURATION_SECONDS,
    mediaPasteEventName: 'comment_media_paste',
    mediaSelectEventName: 'comment_media_select',
    showError,
  });
  const {
    attachMedia,
    attachPastedImage,
    beginReply: beginInlineReply,
    body: commentBody,
    canSubmit: canSubmitComment,
    clearMedia,
    closeGiphy,
    giphyGif,
    giphyVisible,
    handleComposerFocusChange,
    inputRef: commentInputRef,
    openGiphy,
    profileMentions: commentMentions,
    replyTarget,
    resetComposer: resetCommentComposer,
    selectGiphy,
    setBody: setCommentBody,
    setProfileMentions: setCommentMentions,
    setReplyTarget,
    setUploadStatus: setCommentUploadStatus,
    uploadMedia,
    uploadStatus: commentUploadStatus,
  } = commentComposerState;
  const [commentModalVisible, setCommentModalVisible] = useState(false);
  const [commentModalPost, setCommentModalPost] = useState<PublicPost | null>(
    null,
  );
  const [modalReplyTarget, setModalReplyTarget] =
    useState<CommentReplyTarget | null>(null);
  const [videoViewerPostId, setVideoViewerPostId] = useState<string | null>(
    null,
  );
  const [videoViewerInitialPost, setVideoViewerInitialPost] =
    useState<PublicPost | null>(null);
  const [photoViewerPostId, setPhotoViewerPostId] = useState<string | null>(
    null,
  );
  const [photoViewerInitialPost, setPhotoViewerInitialPost] =
    useState<PublicPost | null>(null);
  const [photoViewerInitialIndex, setPhotoViewerInitialIndex] = useState(0);
  const rankedPublicFeed = usePublicPosts(
    user?.uid,
    undefined,
    'ranked',
    signedIn && Boolean(videoViewerPostId || photoViewerPostId),
  );
  const [newCommentsAvailable, setNewCommentsAvailable] = useState(false);
  const insets = useSafeAreaInsets();
  const scrollViewRef = useRef<ScrollView | null>(null);
  const commentsPanelOffsetRef = useRef(0);
  const commentOffsetsRef = useRef<Record<string, number>>({});
  const scrolledToTargetCommentRef = useRef<string | null>(null);
  const hasInitializedCommentsRef = useRef(false);
  const isNearCommentBottomRef = useRef(true);
  const previousCommentIdsRef = useRef<Set<string>>(new Set());
  const activePost = signedIn ? postState.post : publicPostState.post;
  const activeComments = signedIn
    ? commentsState.comments
    : publicPostState.comments;
  const activeLoading = signedIn ? postState.loading : publicPostState.loading;
  const activeError = signedIn ? postState.error : publicPostState.error;
  const { width: viewportWidth } = useWindowDimensions();
  const useReplyModal = viewportWidth < COMMENT_MODAL_COMPACT_BREAKPOINT;
  const videoViewerPosts = activePost
    ? [
        activePost,
        ...rankedPublicFeed.posts.filter((post) => post.id !== activePost.id),
      ]
    : rankedPublicFeed.posts;

  const scrollToLatestComment = useCallback((animated = true) => {
    setTimeout(() => {
      scrollViewRef.current?.scrollTo({
        animated,
        y: Math.max(commentsPanelOffsetRef.current - spacing.md, 0),
      });
    }, 80);
  }, []);

  const scrollToTop = useCallback((animated = false) => {
    scrollViewRef.current?.scrollTo({ animated, y: 0 });
  }, []);

  const scrollToComment = useCallback((commentId: string, animated = true) => {
    const commentOffset = commentOffsetsRef.current[commentId];

    if (typeof commentOffset !== 'number') {
      return false;
    }

    scrollViewRef.current?.scrollTo({
      animated,
      y: Math.max(
        commentsPanelOffsetRef.current + commentOffset - spacing.lg,
        0,
      ),
    });

    return true;
  }, []);

  const handleCommentLayout = useCallback(
    (commentId: string, y: number) => {
      commentOffsetsRef.current[commentId] = y;

      if (
        targetCommentId === commentId &&
        scrolledToTargetCommentRef.current !== targetCommentId
      ) {
        setTimeout(() => {
          if (scrollToComment(commentId)) {
            scrolledToTargetCommentRef.current = commentId;
          }
        }, 80);
      }
    },
    [scrollToComment, targetCommentId],
  );

  const handleCommentsPanelLayout = useCallback(
    (event: LayoutChangeEvent) => {
      commentsPanelOffsetRef.current = event.nativeEvent.layout.y;

      if (
        targetCommentId &&
        scrolledToTargetCommentRef.current !== targetCommentId
      ) {
        setTimeout(() => {
          if (scrollToComment(targetCommentId)) {
            scrolledToTargetCommentRef.current = targetCommentId;
          }
        }, 80);
      }
    },
    [scrollToComment, targetCommentId],
  );

  useEffect(() => {
    commentOffsetsRef.current = {};
    scrolledToTargetCommentRef.current = null;
    hasInitializedCommentsRef.current = false;
    previousCommentIdsRef.current = new Set();
    setNewCommentsAvailable(false);
  }, [postId, targetCommentId]);

  useEffect(() => {
    if (!postId || targetCommentId) {
      return undefined;
    }

    const initialTimer = setTimeout(() => scrollToTop(false), 0);
    const layoutTimer = setTimeout(() => scrollToTop(false), 120);

    return () => {
      clearTimeout(initialTimer);
      clearTimeout(layoutTimer);
    };
  }, [activePost?.id, postId, scrollToTop, targetCommentId]);

  useEffect(() => {
    if (
      !targetCommentId ||
      scrolledToTargetCommentRef.current === targetCommentId ||
      !activeComments.some((comment) => comment.id === targetCommentId)
    ) {
      return;
    }

    setTimeout(() => {
      if (scrollToComment(targetCommentId)) {
        scrolledToTargetCommentRef.current = targetCommentId;
      }
    }, 80);
  }, [activeComments, scrollToComment, targetCommentId]);

  useEffect(() => {
    const comments = activeComments;
    const currentCommentIds = new Set(comments.map((comment) => comment.id));

    if (!hasInitializedCommentsRef.current) {
      previousCommentIdsRef.current = currentCommentIds;
      hasInitializedCommentsRef.current = true;
      return;
    }

    const newComments = comments.filter(
      (comment) => !previousCommentIdsRef.current.has(comment.id),
    );

    previousCommentIdsRef.current = currentCommentIds;

    if (newComments.length === 0) {
      return;
    }

    const hasOwnNewComment = newComments.some(
      (comment) => comment.authorUid === user?.uid,
    );

    if (hasOwnNewComment) {
      setNewCommentsAvailable(false);
      scrollToLatestComment();
      return;
    }

    if (!isNearCommentBottomRef.current) {
      setNewCommentsAvailable(true);
    }
  }, [activeComments, scrollToLatestComment, user?.uid]);

  if (activeLoading) {
    return <LoadingView message="Loading post..." />;
  }

  if (activeError) {
    return (
      <Screen>
        <ErrorView message={activeError.message} />
      </Screen>
    );
  }

  if (!postId || !activePost) {
    return (
      <Screen scroll={false} contentStyle={styles.notFoundContent}>
        <InternalNotFound message="This post may have moved, been deleted, or is no longer available." />
      </Screen>
    );
  }

  const fullName =
    profileQuery.data?.fullName || user?.email || 'Frienzzi user';
  const avatarInitial = fullName.slice(0, 1).toUpperCase();
  const avatarURL = getProfileAvatarURL(profileQuery.data, 'thumb');

  function beginReply(target: CommentReplyTarget) {
    if (useReplyModal) {
      setModalReplyTarget(target);
      setCommentModalVisible(true);
      return;
    }

    beginInlineReply(target);
  }

  async function submitComment() {
    const body = commentBody.trim();

    if (!user?.uid) {
      showError(new Error('You must be signed in to comment.'));
      return;
    }

    if (commentUploadStatus || createComment.isPending) {
      return;
    }

    if (!body && !uploadMedia && !giphyGif) {
      showError(
        new Error('Add a comment, photo, video, or GIF before posting.'),
      );
      return;
    }

    if (body.length > MAX_COMMENT_BODY_LENGTH) {
      showError(new Error('Comments must be 1,000 characters or fewer.'));
      return;
    }

    const uploadLabel =
      uploadMedia?.kind === 'image'
        ? 'photo'
        : uploadMedia?.kind === 'video'
          ? 'video'
          : null;
    let finalBody = body;
    const commentInput = {
      authorUid: user.uid,
      authorFounderBadge: profileQuery.data?.founderBadge === true,
      authorFullName: fullName,
      authorPhotoURL: avatarURL,
      authorUsername: profileQuery.data?.username ?? null,
      authorVerifiedBadge:
        profileQuery.data?.verification?.badge?.verified === true,
      body: finalBody,
      giphyGif,
      mediaAsset: uploadMedia?.asset ?? null,
      mediaKind: uploadMedia?.kind ?? null,
      parentCommentId: replyTarget?.parentCommentId ?? null,
      postId,
      profileMentions: commentMentions,
      replyToCommentId: replyTarget?.replyToCommentId ?? null,
    };

    try {
      setCommentUploadStatus(
        uploadLabel ? `Preparing ${uploadLabel}...` : null,
      );

      if (!(await isNetworkAvailable())) {
        await queueCreateComment(commentInput);
        trackEvent('comment_queue', {
          has_media: Boolean(uploadMedia || giphyGif),
          has_text: Boolean(body),
          media_kind: uploadMedia?.kind ?? (giphyGif ? 'gif' : 'none'),
        });
        resetCommentComposer();
        scrollToLatestComment();
        showSuccess(
          'Your comment will upload when your connection is back.',
          'Comment queued',
        );
        return;
      }

      if (finalBody) {
        setCommentUploadStatus('Checking spelling...');

        try {
          const correction = await maybeCorrectWriting({
            profile: profileQuery.data,
            surface: 'comment',
            text: finalBody,
          });

          finalBody = correction.text;
          commentInput.body = finalBody;

          if (correction.changed) {
            trackEvent('writing_assist_correction_apply', {
              surface: 'comment',
            });
          }
        } catch {
          trackEvent('writing_assist_correction_skip', {
            reason: 'error',
            surface: 'comment',
          });
        }

        setCommentUploadStatus(
          uploadLabel ? `Preparing ${uploadLabel}...` : null,
        );
      }

      createComment.mutate(commentInput, {
        onError: (error) => {
          if (isRecoverableNetworkError(error)) {
            void queueCreateComment(commentInput);
            resetCommentComposer();
            scrollToLatestComment();
            showSuccess(
              'Your comment will upload when your connection is back.',
              'Comment queued',
            );
            return;
          }

          showError(error, 'Unable to post your comment. Please try again.');
        },
        onSuccess: () => {
          trackEvent('comment_create', {
            has_media: Boolean(uploadMedia || giphyGif),
            has_text: Boolean(body),
            media_kind: uploadMedia?.kind ?? (giphyGif ? 'gif' : 'none'),
          });
          resetCommentComposer();
          scrollToLatestComment();
          showSuccess('Your comment is posting.', 'Comment posted');
        },
      });
    } catch (error) {
      if (isRecoverableNetworkError(error)) {
        await queueCreateComment(commentInput);
        resetCommentComposer();
        scrollToLatestComment();
        showSuccess(
          'Your comment will upload when your connection is back.',
          'Comment queued',
        );
        return;
      }

      showError(error, 'Unable to post your comment. Please try again.');
    } finally {
      setCommentUploadStatus(null);
    }
  }

  async function removeComment(commentId: string) {
    if (!user?.uid) {
      return;
    }

    try {
      if (!(await isNetworkAvailable())) {
        await queueDeleteComment({ commentId, postId, uid: user.uid });
        trackEvent('comment_delete_queue');
        showSuccess(
          'Your comment deletion will sync when your connection is back.',
          'Delete queued',
        );
        return;
      }

      await deleteComment.mutateAsync({
        commentId,
        postId,
        uid: user.uid,
      });
      trackEvent('comment_delete');
      showSuccess('Your comment was deleted.', 'Comment removed');
    } catch (error) {
      if (isRecoverableNetworkError(error)) {
        await queueDeleteComment({ commentId, postId, uid: user.uid });
        showSuccess(
          'Your comment deletion will sync when your connection is back.',
          'Delete queued',
        );
        return;
      }

      showError(error, 'Unable to delete that comment.');
    }
  }

  function handleScroll(event: NativeSyntheticEvent<NativeScrollEvent>) {
    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
    const distanceFromBottom =
      contentSize.height - (contentOffset.y + layoutMeasurement.height);
    const isNearBottom = distanceFromBottom <= NEW_COMMENT_SCROLL_THRESHOLD;

    isNearCommentBottomRef.current = isNearBottom;

    if (isNearBottom) {
      setNewCommentsAvailable(false);
    }
  }

  const commentComposerProps: Omit<CommentComposerProps, 'compact'> = {
    avatarInitial,
    avatarURL,
    body: commentBody,
    canSubmit: canSubmitComment,
    currentUid: user?.uid,
    giphyGif,
    inputRef: commentInputRef,
    loading: Boolean(commentUploadStatus || createComment.isPending),
    maxLength: MAX_COMMENT_BODY_LENGTH,
    mentionScope: { kind: 'comment', postId },
    profileMentions: commentMentions,
    uploadMedia,
    uploadStatus: commentUploadStatus,
    onAttachPhoto: () => {
      void attachMedia('image');
    },
    onAttachVideo: () => {
      void attachMedia('video');
    },
    onChangeBody: setCommentBody,
    onChangeFocus: handleComposerFocusChange,
    onChangeMentions: setCommentMentions,
    onClearMedia: clearMedia,
    onOpenGiphy: openGiphy,
    onPasteImageAsset: attachPastedImage,
    onSubmit: () => {
      void submitComment();
    },
  };
  const commentComposer = <CommentComposer {...commentComposerProps} compact />;
  const replyComposer = replyTarget ? (
    <ReplyCommentComposer
      {...commentComposerProps}
      compact={useReplyModal}
      replyTarget={replyTarget}
      onCancelReply={() => setReplyTarget(null)}
    />
  ) : null;

  const appTabBarOffset =
    APP_BOTTOM_TAB_BAR_BASE_HEIGHT + getStableBottomInset(insets.bottom);
  const scrollBottomPadding = signedIn
    ? appTabBarOffset + spacing.xl
    : spacing.xl;

  return (
    <SafeAreaView edges={['left', 'right']} style={styles.safeArea}>
      <AuthenticatedRouteTransition>
        <ScrollView
          ref={scrollViewRef}
          style={styles.scrollView}
          contentContainerStyle={[
            styles.scrollContent,
            { paddingBottom: scrollBottomPadding },
          ]}
          keyboardShouldPersistTaps="handled"
          refreshControl={
            <RefreshControl
              refreshing={
                signedIn
                  ? postState.refreshing || commentsState.refreshing
                  : publicPostState.refreshing
              }
              tintColor={colors.primary}
              onRefresh={() => {
                if (signedIn) {
                  postState.refresh();
                  commentsState.refresh();
                } else {
                  publicPostState.refresh();
                }
              }}
            />
          }
          scrollEventThrottle={16}
          showsVerticalScrollIndicator={false}
          onScroll={handleScroll}
        >
          <View style={styles.content}>
            <PostCard
              openDetailOnPress={false}
              onDeleted={() => router.replace('/home')}
              onCommentPress={() => {
                setCommentModalPost(activePost);
                setCommentModalVisible(true);
              }}
              onPhotoPress={(post, photoIndex) => {
                setPhotoViewerInitialPost(post);
                setPhotoViewerPostId(post.id);
                setPhotoViewerInitialIndex(photoIndex);
              }}
              onVideoPress={(post) => {
                setVideoViewerInitialPost(post);
                setVideoViewerPostId(post.id);
              }}
              post={activePost}
              showCommentCount
            />

            <View
              style={styles.commentsPanel}
              onLayout={handleCommentsPanelLayout}
            >
              <View style={styles.commentsHeader}>
                <View style={styles.commentsTitleRow}>
                  <Text style={styles.heading}>Comments</Text>
                  <View
                    style={[
                      styles.commentCountBadge,
                      activeComments.length === 0
                        ? styles.commentCountBadgeEmpty
                        : null,
                    ]}
                  >
                    <Text
                      style={[
                        styles.commentCountText,
                        activeComments.length === 0
                          ? styles.commentCountTextEmpty
                          : null,
                      ]}
                    >
                      {activeComments.length}
                    </Text>
                  </View>
                </View>
                <CommentSortControl
                  compact={viewportWidth < 520}
                  value={sortMode}
                  onChange={setSortMode}
                />
              </View>
              {signedIn && !replyTarget ? (
                <CommentComposerCard>{commentComposer}</CommentComposerCard>
              ) : null}
              {signedIn && commentsState.loading ? (
                <Text style={styles.mutedText}>Loading comments...</Text>
              ) : null}
              {signedIn && commentsState.error ? (
                <ErrorView message={commentsState.error.message} />
              ) : null}
              {!(signedIn && commentsState.loading) &&
              !(signedIn && commentsState.error) &&
              activeComments.length === 0 ? (
                <View style={styles.emptyState}>
                  <Text style={styles.emptyTitle}>No comments yet</Text>
                  <Text style={styles.emptyText}>
                    Be the first to comment on this post.
                  </Text>
                </View>
              ) : null}
              <PostCommentThreadList
                comments={activeComments}
                currentUid={user?.uid}
                deleteDisabled={deleteComment.isPending}
                postAuthorUid={activePost.authorUid}
                postId={postId}
                replyComposer={replyComposer}
                replyTarget={replyTarget}
                sortMode={sortMode}
                onCommentLayout={handleCommentLayout}
                onDelete={(commentId) => {
                  void removeComment(commentId);
                }}
                onReply={beginReply}
              />
              {!signedIn ? (
                <View style={styles.signInPanel}>
                  <Text style={styles.signInTitle}>Join the conversation</Text>
                  <Text style={styles.signInText}>
                    Sign in or create an account to comment, react, follow, and
                    share on Frienzzi.
                  </Text>
                  <Pressable
                    accessibilityRole="button"
                    onPress={() => router.push('/sign-in')}
                    style={styles.signInButton}
                  >
                    <Text style={styles.signInButtonText}>Sign in</Text>
                  </Pressable>
                </View>
              ) : null}
            </View>
          </View>
        </ScrollView>
      </AuthenticatedRouteTransition>
      {signedIn && newCommentsAvailable ? (
        <Pressable
          accessibilityLabel="Scroll to new comments"
          accessibilityRole="button"
          onPress={() => {
            setNewCommentsAvailable(false);
            scrollToLatestComment();
          }}
          style={[
            styles.newCommentsButton,
            { bottom: appTabBarOffset + spacing.md },
          ]}
        >
          <Text style={styles.newCommentsText}>New comments</Text>
        </Pressable>
      ) : null}
      {signedIn ? <AuthenticatedBottomTabBar fixed /> : null}
      <CommentComposeModal
        initialReplyTarget={modalReplyTarget}
        post={signedIn ? (commentModalPost ?? activePost) : null}
        visible={signedIn && commentModalVisible}
        onClose={() => {
          setCommentModalVisible(false);
          setCommentModalPost(null);
          setModalReplyTarget(null);
        }}
      />
      <PostVideoViewerModal
        key={videoViewerPostId ?? 'post-video-viewer'}
        hasMorePosts={rankedPublicFeed.hasMore}
        initialPost={videoViewerInitialPost}
        initialPostId={videoViewerPostId}
        loadingMorePosts={rankedPublicFeed.loadingMore}
        posts={videoViewerPosts}
        visible={Boolean(videoViewerPostId)}
        onClose={() => {
          setVideoViewerPostId(null);
          setVideoViewerInitialPost(null);
        }}
        onCommentPress={(post) => {
          setCommentModalPost(post);
          setCommentModalVisible(true);
        }}
        onLoadMorePosts={rankedPublicFeed.loadMore}
      />
      <PostPhotoViewerModal
        key={
          photoViewerPostId
            ? `${photoViewerPostId}:${photoViewerInitialIndex}`
            : 'post-photo-viewer'
        }
        hasMorePosts={rankedPublicFeed.hasMore}
        initialPhotoIndex={photoViewerInitialIndex}
        initialPost={photoViewerInitialPost}
        initialPostId={photoViewerPostId}
        loadingMorePosts={rankedPublicFeed.loadingMore}
        posts={videoViewerPosts}
        visible={Boolean(photoViewerPostId)}
        onClose={() => {
          setPhotoViewerPostId(null);
          setPhotoViewerInitialPost(null);
          setPhotoViewerInitialIndex(0);
        }}
        onCommentPress={(post) => {
          setCommentModalPost(post);
          setCommentModalVisible(true);
        }}
        onLoadMorePosts={rankedPublicFeed.loadMore}
      />
      <GiphyPickerModal
        visible={giphyVisible}
        onClose={closeGiphy}
        onSelect={selectGiphy}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  comment: {
    alignItems: 'flex-start',
    borderTopColor: colors.border,
    borderTopWidth: 1,
    flexDirection: 'row',
    gap: spacing.md,
    paddingTop: spacing.lg,
  },
  commentGroup: {
    gap: spacing.sm,
  },
  commentReply: {
    borderTopWidth: 0,
    marginLeft: spacing.xl,
    paddingLeft: spacing.sm,
    paddingTop: spacing.sm,
  },
  commentAuthor: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '900',
  },
  commentAuthorButton: {
    flexShrink: 1,
    minWidth: 0,
  },
  commentAuthorRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.xs,
    minWidth: 0,
  },
  commentAuthorUsername: {
    color: colors.mutedText,
    flexShrink: 1,
    fontSize: 14,
  },
  commentAvatar: {
    alignItems: 'center',
    backgroundColor: colors.accentSoft,
    borderRadius: 20,
    height: 40,
    justifyContent: 'center',
    overflow: 'hidden',
    width: 40,
  },
  commentAvatarImage: {
    height: '100%',
    width: '100%',
  },
  commentAvatarText: {
    color: colors.secondary,
    fontSize: 16,
    fontWeight: '900',
  },
  commentBody: {
    flex: 1,
    gap: spacing.sm,
  },
  commentActions: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
    justifyContent: 'space-between',
    minHeight: 36,
  },
  commentActionLeft: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.xs,
    flexWrap: 'wrap',
  },
  commentAge: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '800',
    minWidth: 28,
  },
  commentHeader: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.sm,
    justifyContent: 'space-between',
  },
  commentHeaderText: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  commentHeaderActions: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.xs,
  },
  commentMedia: {
    aspectRatio: 1,
    backgroundColor: colors.surfaceTint,
    borderRadius: 8,
    maxWidth: 320,
    overflow: 'hidden',
    width: '100%',
  },
  commentMediaImage: {
    height: '100%',
    width: '100%',
  },
  commentMediaWrap: {
    gap: spacing.xs,
  },
  commentReactionSummary: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    minHeight: 32,
  },
  reactionCountText: {
    marginLeft: -4,
  },
  commentReplyButton: {
    borderRadius: 999,
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
  },
  commentReplyButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  commentReplyButtonText: {
    color: colors.secondary,
    fontSize: 12,
    fontWeight: '900',
  },
  commentText: {
    color: colors.text,
    fontSize: 15,
    lineHeight: 22,
  },
  commentsHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    justifyContent: 'space-between',
  },
  commentCountBadge: {
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderRadius: 999,
    justifyContent: 'center',
    minHeight: 28,
    minWidth: 38,
    paddingHorizontal: spacing.sm,
  },
  commentCountBadgeEmpty: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderWidth: 1,
  },
  commentCountText: {
    color: colors.surface,
    fontSize: 13,
    fontWeight: '900',
  },
  commentCountTextEmpty: {
    color: colors.secondary,
  },
  commentsTitleRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
    minWidth: 0,
  },
  commentsPanel: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.md,
    padding: spacing.sm,
  },
  signInPanel: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.sm,
    padding: spacing.md,
  },
  signInTitle: {
    color: colors.text,
    fontSize: 18,
    fontWeight: '900',
  },
  signInText: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
  },
  signInButton: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: colors.primary,
    borderRadius: 999,
    justifyContent: 'center',
    minHeight: 40,
    paddingHorizontal: spacing.lg,
  },
  signInButtonText: {
    color: colors.surface,
    fontSize: 14,
    fontWeight: '900',
  },
  content: {
    alignSelf: 'center',
    gap: spacing.lg,
    maxWidth: 640,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.lg,
    width: '100%',
  },
  notFoundContent: {
    maxWidth: '100%',
    padding: 0,
  },
  deleteCommentButton: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    height: 34,
    justifyContent: 'center',
    width: 34,
  },
  deleteCommentButtonDisabled: {
    opacity: 0.45,
  },
  deleteCommentButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  editCommentButton: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    height: 34,
    justifyContent: 'center',
    width: 34,
  },
  editCommentButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  commentEditedButton: {
    alignItems: 'center',
    borderRadius: 999,
    flexDirection: 'row',
    gap: 3,
    minHeight: 28,
    paddingHorizontal: 2,
  },
  commentEditedButtonPressed: {
    opacity: 0.78,
  },
  commentEditedText: {
    color: colors.primary,
    fontSize: 12,
    fontWeight: '900',
  },
  emptyState: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.sm,
    padding: spacing.md,
  },
  emptyText: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
  },
  emptyTitle: {
    color: colors.text,
    fontSize: 17,
    fontWeight: '900',
  },
  giphyAttribution: {
    color: colors.mutedText,
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  heading: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
  },
  mutedText: {
    color: colors.mutedText,
    fontSize: 14,
  },
  newCommentsButton: {
    alignSelf: 'center',
    backgroundColor: colors.primary,
    borderRadius: 999,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    position: 'absolute',
    shadowColor: colors.primary,
    shadowOffset: { height: 6, width: 0 },
    shadowOpacity: 0.22,
    shadowRadius: 14,
  },
  newCommentsText: {
    color: colors.surface,
    fontSize: 13,
    fontWeight: '900',
  },
  safeArea: {
    backgroundColor: colors.background,
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    width: '100%',
  },
  scrollView: {
    flex: 1,
    width: '100%',
  },
});
